import { alpha, makeStyles } from '@material-ui/core/styles';

export default makeStyles(theme => ({
  pageHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 16, marginBottom: 24,
  },
  heading: { display: 'flex', alignItems: 'center', gap: 14 },
  headingIcon: {
    width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.09),
  },
  subtitle: { marginTop: 6, fontSize: 12, lineHeight: 1.9, color: theme.palette.text.secondary },
  refreshButton: { borderRadius: 8, padding: '8px 16px', whiteSpace: 'nowrap' },
  card: {
    borderRadius: 14, border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
    boxShadow: `0 4px 22px ${alpha(theme.palette.common.black, 0.035)}`,
    height: '100%', display: 'flex', flexDirection: 'column',
  },
  metricCard: { position: 'relative', overflow: 'hidden' },
  metricContent: { padding: '24px !important' },
  metricHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  metricTitle: { color: theme.palette.text.secondary, fontSize: 13, fontWeight: 500 },
  metricIcon: {
    flexShrink: 0, width: 46, height: 46, borderRadius: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    '& svg': { fontSize: 25 },
  },
  metric: { fontSize: 36, fontWeight: 700, margin: '10px 0 8px', lineHeight: 1.3, letterSpacing: -0.8 },
  metricDetail: { fontSize: 11, lineHeight: 1.9, minHeight: 21, color: theme.palette.text.secondary },
  metricAccent: { position: 'absolute', bottom: 0, left: 24, right: 24, height: 3, borderRadius: '3px 3px 0 0' },
  cardHeader: {
    padding: '24px 24px 16px', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
    '& .Cmt-content-head': { minWidth: 0, flex: '1 1 220px' },
    '& .Cmt-header-content': { minWidth: 0 },
    '& .Cmt-action-default-menu': { marginLeft: 0 },
  },
  cardTitle: { fontSize: 15, fontWeight: 700, lineHeight: 1.9 },
  cardSubtitle: { color: `${theme.palette.text.secondary} !important`, fontSize: '11px !important', lineHeight: 1.9 },
  chartContent: { padding: '0 20px 20px !important', flex: 1, minWidth: 0 },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
    borderRadius: 7, fontSize: 11, fontWeight: 500, lineHeight: 1.6,
    whiteSpace: 'nowrap', color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.07),
    '& svg': { fontSize: 15 },
  },
  chartMeta: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 8, margin: '0 4px 16px',
    color: theme.palette.text.secondary, fontSize: 11,
  },
  legend: { display: 'inline-flex', alignItems: 'center', gap: 7 },
  legendDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  chart: {
    flip: false, direction: 'ltr',
    '& .recharts-text': { fontFamily: theme.typography.fontFamily, fontSize: 10, fill: theme.palette.text.secondary },
    '& .recharts-surface': { overflow: 'visible' },
  },
  tooltip: {
    flip: false, direction: 'rtl', padding: '12px 16px', borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
    '& p': { fontSize: 12, lineHeight: 2 },
  },
  tooltipValue: { fontWeight: 700, color: theme.palette.primary.main },
  tableDetails: {
    borderTop: `1px solid ${theme.palette.divider}`, marginTop: 12,
    '&[open] > summary $expandIcon': { transform: 'rotate(180deg)' },
  },
  summary: {
    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
    padding: '16px 4px 0', fontSize: 11, color: theme.palette.text.secondary,
    listStyle: 'none', '&::-webkit-details-marker': { display: 'none' },
    '&:hover': { color: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 4 },
  },
  expandIcon: { marginLeft: 'auto', transition: 'transform 180ms', fontSize: 18 },
  tableScroll: {
    maxHeight: 300, overflow: 'auto', marginTop: 16, borderRadius: 8,
    '& th': { backgroundColor: theme.palette.background.default, fontSize: 11, fontWeight: 700 },
    '& td': { fontSize: 12, padding: '10px 16px', borderColor: alpha(theme.palette.text.primary, 0.05) },
    '& tbody tr:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.025) },
  },
  monthSelect: {
    minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 8, fontSize: 12 },
    '& .MuiInputLabel-root': { fontSize: 12 },
  },
  emptyState: {
    padding: '36px 20px', textAlign: 'center', color: theme.palette.text.secondary,
    '& svg': { fontSize: 36, marginBottom: 12, opacity: 0.45 },
    '& p': { fontSize: 12, lineHeight: 1.9 },
  },
  status: {
    padding: '44px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 16, textAlign: 'center', '& p': { fontSize: 13, lineHeight: 2 },
  },
  footer: {
    display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 20, padding: '0 4px',
    color: theme.palette.text.secondary,
    '& svg': { fontSize: 16, marginTop: 3, flexShrink: 0 },
    '& p': { fontSize: 11, lineHeight: 2 },
  },
  [theme.breakpoints.down('xs')]: {
    metricContent: { padding: '20px !important' },
    metric: { fontSize: 30 },
    cardHeader: { padding: '20px 16px 16px' },
    chartContent: { padding: '0 12px 16px !important' },
    headingIcon: { width: 42, height: 42, borderRadius: 12 },
    refreshButton: { width: '100%' },
  },
}));
